import { toast } from "react-toastify";

import {
  GEO_MODEL_PROJECTION_EXTENSION,
  GEO_MODEL_RASTER_EXTENSIONS,
  GeoModelFileExtension
} from "constants/mapLayers.constants";

import { Calculation, Field } from "../../models/mapLayer";
import { RasterWithFile } from "../GeoMapLoaderModal.types";
import { findBestFieldMatch } from "../utils/geoAttributeMatcher";

/**
 * Processes uploaded raster files and creates map objects with stored File references
 * for later batch upload. This is shared between create and edit mode handlers.
 */
export function processRasterFiles(
  files: File[],
  mapTypes: Field[]
): Array<{ Raster?: RasterWithFile; Calculation?: Calculation }> {
  const tifAndZFiles = files.filter((file) =>
    GEO_MODEL_RASTER_EXTENSIONS.some((ext) => file.name.endsWith(ext))
  );
  const projFiles = files.filter((file) =>
    file.name.endsWith(GEO_MODEL_PROJECTION_EXTENSION)
  );

  const newMaps: Array<{ Raster?: RasterWithFile; Calculation?: Calculation }> = [];

  for (const file of tifAndZFiles) {
    let projFile: File | null = null;
    let overrideFileName: string | undefined = undefined;

    // Special handling for .z files
    if (file.name.endsWith(GeoModelFileExtension.Z)) {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const path = (file as any).path;
      if (projFiles.length === 0) {
        toast.warn("Note: .z files additionally require a .prj file to be uploaded");
        continue;
      }

      const fileNameWithoutExtension = file.name.replace(GeoModelFileExtension.Z, "");
      for (const projectionFile of projFiles) {
        const projFileNameWithoutExtension = projectionFile.name.replace(
          GEO_MODEL_PROJECTION_EXTENSION,
          ""
        );
        if (projFileNameWithoutExtension === fileNameWithoutExtension) {
          projFile = projectionFile;
          break;
        }
      }

      if (!projFile) {
        projFile = projFiles[0];
      }

      if (path && path.length > 0) {
        const folder = path.split("/");
        if (folder.length > 2) {
          overrideFileName = folder[1] + GeoModelFileExtension.Z;
        }
      }
    }

    // Find best matching field template
    const bestMatch = findBestFieldMatch(overrideFileName ?? file.name, mapTypes);
    const noFieldMatch = !bestMatch;
    const resolvedMatch = bestMatch ?? mapTypes[0];
    const expression = resolvedMatch?.expression ?? "";

    // Create a temporary map object with the File stored for later upload
    const tempId = `temp-${Date.now()}-${Math.random()}`;

    const map: { Raster: RasterWithFile } = {
      Raster: {
        name: resolvedMatch?.name ?? file.name,
        id: tempId,
        filepath: file.name,
        createdDate: {
          $date: new Date().toISOString()
        },
        createdBy: "",
        transformer: expression
          ? {
              type: "ExpressionTransform" as const,
              expression
            }
          : undefined,
        unit: resolvedMatch?.unit,
        min: resolvedMatch?.min,
        max: resolvedMatch?.max,
        // Store File objects for batch upload
        _file: file,
        _projFile: projFile ?? undefined,
        _overrideFileName: overrideFileName,
        _noFieldMatch: noFieldMatch || undefined
      }
    };

    newMaps.push(map);
  }

  return newMaps;
}
